import { CreatePaymentRequest } from "@humanex/interfaces";
import { getErrorMessage, toast, useI18n } from "@humanex/ui/libs";
import { useMutation, useQuery } from "@tanstack/react-query";

import { creditApiClient } from "@/libs/api";

export const creditKeys = {
  all: ["credit"] as const,
  my: () => [...creditKeys.all, "my"] as const,
  transactions: () => [...creditKeys.all, "transactions"] as const,
  orgCredit: (orgId: string) => [...creditKeys.all, "org", orgId] as const,
  orgTransactions: (orgId: string) => [...creditKeys.all, "org", orgId, "transactions"] as const
};

export function useMyCreditQuery() {
  return useQuery({
    queryKey: creditKeys.my(),
    queryFn: () => creditApiClient.getMy(),
    staleTime: 5 * 60 * 1000
  });
}

export function useMyTransactionsQuery() {
  return useQuery({
    queryKey: creditKeys.transactions(),
    queryFn: () => creditApiClient.getMyTransactions(),
    staleTime: 5 * 60 * 1000
  });
}

export function useOrgCreditQuery(orgId: string) {
  return useQuery({
    queryKey: creditKeys.orgCredit(orgId),
    queryFn: () => creditApiClient.getUserCredits(orgId),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000
  });
}

export function useOrgCreditTransactionsQuery(orgId: string) {
  return useQuery({
    queryKey: creditKeys.orgTransactions(orgId),
    queryFn: () => creditApiClient.getUserTransactions(orgId),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000
  });
}

export function useCreatePaymentRequest(
  options: {
    onSuccess?: (result: Awaited<ReturnType<typeof creditApiClient.createPaymentRequest>>) => void;
  } = {}
) {
  const { t } = useI18n();
  return useMutation({
    mutationFn: (data: CreatePaymentRequest) => creditApiClient.createPaymentRequest(data),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}
